import React, { useState, useEffect } from 'react';
import { HelpCircle } from 'lucide-react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const KPI_META = {
  // Marketing
  CPM:          { label: 'CPM',                 desc: 'Cost per 1,000 impressions' },
  CTR:          { label: 'CTR',                 desc: 'Click-Through Rate' },
  CPLC:         { label: 'CPLC',                desc: 'Cost per Landing Click' },
  CPLPV:        { label: 'CPLPV',               desc: 'Cost per Landing-Page View' },
  CPB:          { label: 'CPB',                 desc: 'Cost per Button Click' },
  CPS:          { label: 'CPS (Subs)',          desc: 'Cost per Subscriber' },
  CPS_FT:       { label: 'CPS (Free Trial)',    desc: 'Cost per Free-Trial Sub' },
  CPL:          { label: 'CPL',                 desc: 'Cost per Lead' },
  CPC_CLIENT:   { label: 'CPC (Client)',        desc: 'Cost per Closed Client (Mkt)' },
  // Sales
  CLOSING_RATE: { label: 'Closing Rate',        desc: '% Leads closed' },
  AVG_DEPOSIT:  { label: 'Avg Deposit',         desc: 'Avg deposit per client' },
  RENEWAL_RATE: { label: 'Renewal Rate',        desc: '% Renewing clients' },
  CPL_SALES:    { label: 'CPL (Sales)',         desc: 'Cost per Lead (Sales view)' },
  CPC_CLIENT_SALES: { label: 'CPC (Client)',    desc: 'Cost per Closed Client (Sales)' },
  // Operations
  ER_PUBLIC:    { label: 'E.R (Public)',        desc: 'Engagement Rate — Public channel' },
  ER_VIP:       { label: 'E.R (VIP)',           desc: 'Engagement Rate — VIP channel' },
  SIGNAL_WIN_RATE: { label: 'Signal Win Rate',  desc: '% Winning trade signals' },
  AVG_LOT_CLIENT:  { label: 'Avg Lot/Client',   desc: 'Avg lots traded per client' },
  COMM_CLIENT:  { label: 'Commission/Client',   desc: 'Avg commission per active client' },
  COM_PER_LOT:  { label: 'Com/Lot',             desc: 'Commission per traded lot' },
};

const ZONES = [
  { key: 'excellent', label: 'Excellent', color: '#10B981' },
  { key: 'good',      label: 'Good',      color: '#3B82F6' },
  { key: 'average',   label: 'Average',   color: '#F59E0B' },
  { key: 'poor',      label: 'Poor',      color: '#E63946' },
];

function renderZone(cfg, zoneKey, zoneIdx) {
  const val = cfg?.[zoneKey];
  if (val === undefined || val === null) return '—';
  const unit = cfg.unit || '';
  const lower = !!cfg.lower_better;
  if (lower) {
    const ops = ['≤', '≤', '≤', '>'];
    const op = ops[zoneIdx];
    if (zoneKey === 'poor') return `${unit ? unit + ' ' : ''}> ${cfg.average}`;
    return `${unit ? unit + ' ' : ''}${op} ${val}`;
  }
  if (zoneKey === 'poor') return `${unit ? unit + ' ' : ''}< ${cfg.average}`;
  return `${unit ? unit + ' ' : ''}≥ ${val}`;
}

function KpiRow({ row }) {
  const meta = KPI_META[row.key] || { label: row.label || row.key, desc: row.desc || '' };
  const cfg = row.cfg;
  return (
    <div className="px-2 py-2.5 border-b border-[hsl(var(--border))]/50 last:border-0">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div>
          <p className="text-[12px] font-bold text-white">{row.label || meta.label}</p>
          {(row.desc || meta.desc) && <p className="text-[10px] text-[hsl(var(--text-muted))]">{row.desc || meta.desc}</p>}
        </div>
        {cfg.lower_better && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 border border-blue-500/30 whitespace-nowrap">lower = better</span>
        )}
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {ZONES.map((z, idx) => (
          <div key={z.key}
            className="px-1.5 py-1.5 rounded-md border text-center"
            style={{ background: `${z.color}10`, borderColor: `${z.color}40` }}>
            <p className="text-[8px] uppercase font-bold tracking-wider" style={{ color: z.color }}>{z.label}</p>
            <p className="text-[10px] font-mono font-semibold text-white truncate">{renderZone(cfg, z.key, idx)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * KPI Guide button. Supported usage:
 *  - department: 'marketing'|'sales'|'operation' (auto-fetch from admin)
 *  - metrics: ['CPM',...] from admin
 *  - customKpis: [{key,label,desc,cfg:{excellent,good,average,unit?,lower_better?}}] inline
 *  - groups: [{label,metrics?,department?,customKpis?},...] renders tabs
 */
export default function KpiHelpButton({ department, metrics, customKpis, groups, title = 'KPI Guide' }) {
  const [open, setOpen] = useState(false);
  const [thresholds, setThresholds] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const needsAdmin = (department || (metrics && metrics.length) ||
    (groups && groups.some(g => g.department || (g.metrics && g.metrics.length))));

  useEffect(() => {
    if (!open || thresholds || !needsAdmin) return;
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/admin/kpi-thresholds`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setThresholds(res.data.thresholds || {}))
      .catch(() => setThresholds({}));
  }, [open, thresholds, needsAdmin]);

  // Build rows for one group (resolves metrics/department/customKpis)
  const buildRows = (group) => {
    const out = [];
    if (group.customKpis) {
      group.customKpis.forEach(k => out.push({ key: k.key, label: k.label, desc: k.desc, cfg: k.cfg || k }));
    }
    if (group.metrics && thresholds) {
      group.metrics.forEach(k => {
        for (const dept of ['marketing', 'sales', 'operation']) {
          const cfg = thresholds?.[dept]?.[k];
          if (cfg) { out.push({ key: k, cfg }); break; }
        }
      });
    }
    if (group.department && thresholds) {
      const deptObj = thresholds?.[group.department] || {};
      Object.entries(deptObj).forEach(([k, cfg]) => out.push({ key: k, cfg }));
    }
    return out;
  };

  // Determine groups to render
  const renderGroups = React.useMemo(() => {
    if (groups && groups.length) return groups;
    if (customKpis) return [{ label: title, customKpis }];
    if (department) return [{ label: department.charAt(0).toUpperCase() + department.slice(1), department }];
    if (metrics) return [{ label: 'Metrics', metrics }];
    return [];
  }, [groups, customKpis, department, metrics, title]);

  const activeGroup = renderGroups[activeTab] || renderGroups[0];
  const activeRows = activeGroup ? buildRows(activeGroup) : [];
  const loadingAdmin = needsAdmin && !thresholds;

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); setActiveTab(0); }}
        title="KPI Guide"
        className="inline-flex items-center gap-1 px-2 h-7 rounded-lg text-[hsl(var(--text-muted))] hover:text-white hover:bg-[hsl(var(--surface-elevated))] transition-all border border-transparent hover:border-[hsl(var(--border))]"
        data-testid="kpi-help-btn"
      >
        <HelpCircle size={14} />
        <span className="text-[10px] font-semibold uppercase tracking-wider">KPI Guide</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-[#0a0a0f] border-[hsl(var(--border))] max-w-lg max-h-[85vh] overflow-y-auto p-0"
          data-testid="kpi-help-popup"
        >
          <DialogHeader className="px-5 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/15 to-transparent">
            <DialogTitle className="text-sm font-bold text-white flex items-center gap-2">
              <HelpCircle size={15} className="text-[#E63946]" />
              {title}
            </DialogTitle>
            <p className="text-[10px] text-[hsl(var(--text-muted))] mt-0.5">Threshold values are configured in Admin Panel › KPI Thresholds</p>
          </DialogHeader>

          {/* Tabs (only when more than one group) */}
          {renderGroups.length > 1 && (
            <div className="px-3 pt-2 flex items-center gap-1 overflow-x-auto border-b border-[hsl(var(--border))]/50">
              {renderGroups.map((g, i) => (
                <button
                  key={g.label + i}
                  onClick={() => setActiveTab(i)}
                  className={`px-3 py-2 text-[11px] font-semibold rounded-t-md whitespace-nowrap transition-all ${
                    activeTab === i
                      ? 'text-white bg-[hsl(var(--surface-elevated))] border-b-2 border-[#E63946]'
                      : 'text-[hsl(var(--text-muted))] hover:text-white'
                  }`}
                  data-testid={`kpi-tab-${i}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          )}

          {/* Zone legend */}
          <div className="px-5 pt-3 pb-2 flex items-center gap-3 flex-wrap border-b border-[hsl(var(--border))]/50">
            {ZONES.map(z => (
              <span key={z.key} className="inline-flex items-center gap-1.5 text-[10px] font-semibold" style={{ color: z.color }}>
                <span className="w-2 h-2 rounded-full" style={{ background: z.color }} />
                {z.label}
              </span>
            ))}
          </div>

          <div className="px-3 py-2">
            {loadingAdmin && (
              <p className="px-2 py-6 text-[11px] text-[hsl(var(--text-muted))] text-center">Loading…</p>
            )}
            {!loadingAdmin && activeRows.length === 0 && (
              <p className="px-2 py-6 text-[11px] text-[hsl(var(--text-muted))] text-center">No KPI thresholds configured for this section.</p>
            )}
            {activeRows.map((row, idx) => <KpiRow key={row.key + idx} row={row} />)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============= Pre-defined custom KPI sets (not in admin) =============

// Generic funnel conversion thresholds (Excellent ≥50% / Good ≥30% / Average ≥15% / Poor <15%)
export const CONVERSION_RATE_KPIS = [
  { key: 'CLICKS_CONV', label: 'Clicks Conversion', desc: 'Impressions → Clicks',
    cfg: { excellent: 50, good: 30, average: 15, unit: '%' } },
  { key: 'LPV_CONV',    label: 'LPV Conversion',    desc: 'Clicks → Landing Page View',
    cfg: { excellent: 50, good: 30, average: 15, unit: '%' } },
  { key: 'CTA_CONV',    label: 'CTA Conversion',    desc: 'LPV → CTA Button',
    cfg: { excellent: 50, good: 30, average: 15, unit: '%' } },
  { key: 'SUBS_CONV',   label: 'Subs Conversion',   desc: 'CTA → Subscribers',
    cfg: { excellent: 50, good: 30, average: 15, unit: '%' } },
  { key: 'LEADS_CONV',  label: 'Leads Conversion',  desc: 'Subscribers → Leads',
    cfg: { excellent: 50, good: 30, average: 15, unit: '%' } },
  { key: 'CLOSING_CONV', label: 'Closing Rate',     desc: 'Leads → Closed',
    cfg: { excellent: 50, good: 30, average: 15, unit: '%' } },
];

// Ops funnel conversion thresholds
export const OPS_CONVERSION_KPIS = [
  { key: 'REACH_CONV', label: 'Reach Conv', desc: 'Subscribers → Reach',
    cfg: { excellent: 50, good: 30, average: 15, unit: '%' } },
  { key: 'ENGAGE_CONV', label: 'Engage Conv', desc: 'Reach → Reactions',
    cfg: { excellent: 50, good: 30, average: 15, unit: '%' } },
  { key: 'LEAD_CONV', label: 'Lead Conv', desc: 'Reactions → Leads',
    cfg: { excellent: 50, good: 30, average: 15, unit: '%' } },
  { key: 'CLOSE_CONV', label: 'Closing Rate', desc: 'Leads → Closed',
    cfg: { excellent: 50, good: 30, average: 15, unit: '%' } },
];

// Sales pipeline stuck-rate thresholds (lower = better)
export const PIPELINE_STUCK_KPIS = [
  { key: 'GREETING_STUCK', label: 'Greeting Stuck', desc: '% stuck at Greeting stage',
    cfg: { excellent: 10, good: 25, average: 40, unit: '%', lower_better: true } },
  { key: 'TICK_STUCK',     label: '1 Tick Stuck',   desc: '% stuck at 1 Tick',
    cfg: { excellent: 10, good: 25, average: 40, unit: '%', lower_better: true } },
  { key: 'REG_STUCK',      label: 'Reg Stuck',      desc: '% stuck at Registration',
    cfg: { excellent: 10, good: 25, average: 40, unit: '%', lower_better: true } },
  { key: 'BROKER_STUCK',   label: 'Broker Stuck',   desc: '% stuck at Broker',
    cfg: { excellent: 10, good: 25, average: 40, unit: '%', lower_better: true } },
  { key: 'OFFER_STUCK',    label: 'Offer Stuck',    desc: '% stuck at Offer',
    cfg: { excellent: 10, good: 25, average: 40, unit: '%', lower_better: true } },
];
